import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';
import { useSearch } from '../context/SearchContext';
import { FeaturedProducts } from '../components/FeaturedProducts';

export function AppHomeContent() {
  const navigate = useNavigate();
  const { searchQuery, setSearchQuery } = useSearch();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "What is your shipping policy?",
      answer: "We offer free standard shipping on all orders over $50. Standard shipping takes 3-5 business days. Express shipping is available for an additional fee and delivers within 1-2 business days."
    },
    {
      question: "How do I find my size?",
      answer: "Check our detailed size guide in the product description. We recommend measuring your feet and comparing them to our size chart. If you're between sizes, we recommend going up a size for the best fit."
    },
    {
      question: "What is your return policy?",
      answer: "We offer free returns within 30 days of purchase. Items must be unworn and in original packaging. Simply initiate a return through your account or contact customer service for assistance."
    },
    {
      question: "Do you ship internationally?",
      answer: "Yes, we ship to most countries worldwide. International shipping rates and delivery times vary by location. Customs fees and import duties may apply and are the responsibility of the customer."
    },
    {
      question: "How can I track my order?",
      answer: "Once your order ships, you'll receive a tracking number via email. You can also track your order through your account dashboard or contact our customer service team for assistance."
    }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handlePopularSearch = (term: string) => {
    setSearchQuery(term);
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <main className="pt-4 sm:pt-8">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1556906781-9a412961c28c?w=1200&h=700&fit=crop"
            alt="Hero"
            className="w-full h-[300px] sm:h-[400px] object-cover rounded-lg"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center rounded-lg">
            <div className="px-4 sm:px-8">
              <div className="max-w-lg">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2 sm:mb-4">Make Heads Turn</h2>
                <p className="text-base sm:text-lg text-white mb-4 sm:mb-8">Discover our premium collection of shoes designed for comfort and style.</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/shop" className="bg-purple-600 text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base font-semibold hover:bg-purple-700 transition-colors rounded-full">
                    SHOP NOW
                  </Link>
                  <Link to="/sale" className="bg-red-600 text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base font-semibold hover:bg-red-700 transition-colors rounded-full">
                    VIEW SALE
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white py-8 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for shoes..."
                className="w-full px-4 sm:px-6 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-full focus:outline-none focus:border-purple-600 transition-colors"
              />
              <button 
                type="submit"
                className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 bg-purple-600 text-white p-2 sm:p-3 rounded-full hover:bg-purple-700 transition-colors"
              >
                <Search className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          </form>
          <div className="mt-4 flex justify-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600 flex-wrap">
            <button 
              onClick={() => handlePopularSearch('Popular')}
              className="hover:text-purple-600 transition-colors"
            >
              Popular Searches
            </button>
            <span className="hidden sm:inline">•</span>
            <button 
              onClick={() => handlePopularSearch('Running Shoes')}
              className="hover:text-purple-600 transition-colors"
            >
              Running Shoes
            </button>
            <span className="hidden sm:inline">•</span>
            <button 
              onClick={() => handlePopularSearch('Sneakers')}
              className="hover:text-purple-600 transition-colors"
            >
              Sneakers
            </button>
            <span className="hidden sm:inline">•</span>
            <button 
              onClick={() => handlePopularSearch('Boots')}
              className="hover:text-purple-600 transition-colors"
            >
              Boots
            </button>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16 bg-white">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-2 sm:mb-4">Shop by Category</h2>
        <p className="text-gray-600 text-center mb-8 sm:mb-12 text-sm sm:text-base">Find the perfect shoes for every occasion, from athletic performance to casual elegance.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
          <div className="relative group cursor-pointer overflow-hidden rounded-lg">
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTEt9qoeWtQ7-szhcYl5ZMXqQ_q3nbN2TnPMg&usqp=CAU"
              alt="Men's"
              className="w-full h-[300px] sm:h-[400px] object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all flex flex-col justify-end pb-6 sm:pb-8">
              <div className="text-center">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Men's Shoes</h3>
                <p className="text-white text-sm sm:text-lg mb-2 sm:mb-4">Classic styles with modern performance</p>
                <Link to="/men" className="inline-block bg-white text-black px-4 sm:px-6 py-2 rounded-full hover:bg-gray-100 transition-colors text-sm sm:text-base">
                  Shop Men
                </Link>
              </div>
            </div>
          </div>
          <div className="relative group cursor-pointer overflow-hidden rounded-lg">
            <img
              src="https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500&h=300&fit=crop"
              alt="Women's"
              className="w-full h-[300px] sm:h-[400px] object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all flex flex-col justify-end pb-6 sm:pb-8">
              <div className="text-center">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Women's Shoes</h3>
                <p className="text-white text-sm sm:text-lg mb-2 sm:mb-4">Elegant designs with superior comfort</p>
                <Link to="/women" className="inline-block bg-white text-black px-4 sm:px-6 py-2 rounded-full hover:bg-gray-100 transition-colors text-sm sm:text-base">
                  Shop Women
                </Link>
              </div>
            </div>
          </div>
          <div className="relative group cursor-pointer overflow-hidden rounded-lg">
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRJ24MVbGE37KlJuqtBLc0owhhoTpJ3PyJMlyQ0htaem0tw1hGd4UQ5j0J1&s=10"
              alt="Cargo Pants"
              className="w-full h-[300px] sm:h-[400px] object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all flex flex-col justify-end pb-6 sm:pb-8">
              <div className="text-center">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Cargo Pants</h3>
                <p className="text-white text-sm sm:text-lg mb-2 sm:mb-4">Functional style for everyday adventures</p>
                <Link to="/cargo-pants" className="inline-block bg-white text-black px-4 sm:px-6 py-2 rounded-full hover:bg-gray-100 transition-colors text-sm sm:text-base">
                  Shop Cargo Pants
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Products */}
      <FeaturedProducts />

      {/* Collections Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
          {/* Men's Collection */}
          <div className="relative h-[300px] sm:h-[400px] overflow-hidden rounded-2xl">
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTEt9qoeWtQ7-szhcYl5ZMXqQ_q3nbN2TnPMg&usqp=CAU"
              alt="Men's Collection"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center">
              <div className="p-6 sm:p-8">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2 sm:mb-4">Men's Collection</h2>
                <p className="text-base sm:text-lg text-white/90 mb-4 sm:mb-6">Discover our latest men's shoes collection.</p>
                <button className="bg-purple-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full font-semibold hover:bg-purple-700 transition-colors text-sm sm:text-base">
                  Shop Men's
                </button>
              </div>
            </div>
          </div>

          {/* Women's Collection */}
          <div className="relative h-[300px] sm:h-[400px] overflow-hidden rounded-2xl">
            <img
              src="https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500&h=300&fit=crop"
              alt="Women's Collection"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center">
              <div className="p-6 sm:p-8">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2 sm:mb-4">Women's Collection</h2>
                <p className="text-base sm:text-lg text-white/90 mb-4 sm:mb-6">Explore our curated collection of women's footwear.</p>
                <button className="bg-purple-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full font-semibold hover:bg-purple-700 transition-colors text-sm sm:text-base">
                  Shop Women's
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-2">Frequently Asked Questions</h2>
        <p className="text-gray-600 text-center mb-8">Find answers to common questions about our products and services</p>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(index)}
                className={`w-full flex justify-between items-center p-4 text-left hover:bg-purple-50 transition-colors ${
                  openFaq === index ? 'bg-purple-50' : 'bg-white'
                }`}
              >
                <span className="font-medium text-gray-900">{faq.question}</span>
                {openFaq === index ? (
                  <ChevronUp className="h-5 w-5 text-purple-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </button>
              
              {openFaq === index && (
                <div className="p-4 bg-purple-50 border-t border-gray-200">
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Newsletter */}
      <div className="bg-gray-100 py-8 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-4">Subscribe to our Newsletter</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">Get the latest updates on new products and upcoming sales</p>
          <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-2 sm:gap-0">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-2 sm:py-3 rounded-full sm:rounded-l-lg sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button className="bg-purple-600 text-white px-6 py-2 sm:py-3 rounded-full sm:rounded-l-none sm:rounded-r-lg hover:bg-purple-700 transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}